import React, { memo, useState } from "react";

import RemoveIcon from "@material-ui/icons/DeleteForever";
import IconButton from "@material-ui/core/IconButton";

import RiskLevelSelector from "components/RiskLevelSelector";
import FormBlock from "components/FormBlock";
import ButtonsGroup from "components/ButtonsGroup";
import CommentField from "components/CommentField";
import AddEntryButton from "../AddEntryButton";
import { EVetExamFields, examResultsButtons } from "../options";
import { useExamLockerContext } from "../../ExamLockerContext/ExamLockerContext";
import { useVetExamContext } from "../VetExamProvider/VetExamProvider";
import CustomSelect from "../../CustomSelect/CustomSelect";
import { mapOptions } from "../helper";

import useStyles from "./styles";

const ADD_TRAINER_COMMENT = "Add a Comment to the Trainer";

interface IProps {
  shouldShowConclusionField: boolean;
}

const ExamResults = (props: IProps) => {
  const { shouldShowConclusionField } = props;

  const classes = useStyles();
  const { values, setFieldValue, errors, formFieldsItems } =
    useVetExamContext();
  const { lockExam } = useExamLockerContext();

  const { result, riskLevel, internalNote, commentToTrainer } = values;
  const conclusionId = values?.[EVetExamFields.CONCLUSION];

  const [openTrainerComment, setOpenTrainerComment] = useState<boolean>(
    Boolean(commentToTrainer)
  );

  const handleSelectResult = (value: string) => {
    setFieldValue(EVetExamFields.RESULT, value);
    lockExam();
  };

  const handleSelectRiskLevel = (value: string) => {
    setFieldValue(EVetExamFields.RISK_LEVEL, value);
    lockExam();
  };

  const handleChangeConclusion = (value: string) => {
    setFieldValue(EVetExamFields.CONCLUSION, value);
    lockExam();
  };

  const handleBlurNote = event => {
    setFieldValue(EVetExamFields.INTERNAL_NOTE, event.currentTarget.value);
    lockExam();
  };

  const handleBlurComment = event => {
    setFieldValue(EVetExamFields.COMMENT, event.currentTarget.value);
    lockExam();
  };

  const handleAddTrainerComment = () => {
    setOpenTrainerComment(true);
  };

  const handleRemoveTrainerComment = async () => {
    setOpenTrainerComment(false);
    setFieldValue(EVetExamFields.COMMENT, "");
    lockExam();
  };

  const renderInternalNoteExtraHeaderContent = () => {
    return (
      <span className={classes.internalNote}>&#42;Not visible to trainers</span>
    );
  };

  const renderRemoveTrainerComment = () => {
    return (
      <IconButton
        className={classes.removeButton}
        onClick={handleRemoveTrainerComment}
      >
        <RemoveIcon />
      </IconButton>
    );
  };

  const renderCommentForTrainer = () => {
    if (openTrainerComment || commentToTrainer) {
      return (
        <CommentField
          title={ADD_TRAINER_COMMENT}
          extraHeaderContent={renderRemoveTrainerComment()}
          textareaClassName={classes.textareaField}
          onBlur={handleBlurComment}
          defaultValue={commentToTrainer || ""}
          textareaFontSize={13}
          data-test-id={"examInternalNote"}
          autoFocus={!commentToTrainer}
        />
      );
    }

    return (
      <AddEntryButton
        className={classes.addTrainerComment}
        title={ADD_TRAINER_COMMENT}
        onClick={handleAddTrainerComment}
        isOptional={true}
      />
    );
  };

  return (
    <FormBlock
      className={classes.rootResults}
      title="Exam Results"
      name={EVetExamFields.RESULT}
      isDarkBackground={true}
      isRequired={true}
    >
      <ButtonsGroup
        className={classes.buttonsGroup}
        buttonClassName={classes.buttonsGroupItem}
        title="Result"
        buttons={examResultsButtons}
        selectedValue={result}
        onClickButton={handleSelectResult}
        errorMessage={errors[EVetExamFields.RESULT]}
      />
      <RiskLevelSelector
        riskLevel={riskLevel}
        className={classes.buttonsGroup}
        onChange={handleSelectRiskLevel}
      />
      {shouldShowConclusionField && (
        <CustomSelect
          className={classes.conclusionField}
          options={mapOptions(formFieldsItems.conclusions)}
          label="Conclusion"
          inputLabel={"Conclusion"}
          selectedValue={conclusionId}
          onChangeSelectedValue={handleChangeConclusion}
          dataTestLabel={"examConclusionDropdown"}
          shouldSort={true}
          errorMessage={errors[EVetExamFields.CONCLUSION]}
        />
      )}
      <CommentField
        title="Internal note"
        extraHeaderContent={renderInternalNoteExtraHeaderContent()}
        textareaClassName={classes.textareaField}
        onBlur={handleBlurNote}
        defaultValue={internalNote}
      />
      {renderCommentForTrainer()}
    </FormBlock>
  );
};

export default memo(ExamResults);
